/**
 * Print macros: recorded lists of pages that are rendered again for other production
 * days, a pdf per page. Each page keeps the production or shipping group it was
 * recorded with; only the dates change. Macros are kept on the server (/v2/print-macros)
 * so every kitchen machine sees the same list; the Macros page can still
 * export/import them as json. Two per-browser preferences (open after save,
 * starred only) stay in localStorage.
 *
 * The pages are rendered by the api server (see utils/macro-render-client.js); this
 * module holds the macros and the recording in progress.
 */
import api from '@/api';
import {captureStep, fillProductionToken, inferBaseline, isCompleteTarget, newMacroId, sortedDates} from '@/utils/macro-steps';
import {cleanSchedule} from '@/utils/macro-schedule';

// where macros lived before they moved to the server; read once to carry them over
export const STORAGE_KEY = 'k.fed-print-macros';
export const MIGRATED_KEY = 'k.fed-print-macros-migrated';
export const OPEN_AFTER_SAVE_KEY = 'k.fed-print-macros-open';
export const STARRED_ONLY_KEY = 'k.fed-print-macros-starred-only';
const NO_BASELINE = {dates: []};
const MACROS_URL = 'v2/print-macros';

function getStorage() {
  return typeof window !== 'undefined' && window.localStorage ? window.localStorage : {
    getItem: () => null,
    setItem: () => {
    },
    removeItem: () => {
    },
  };
}

function loadLocalMacros(storage) {
  try {
    const macros = JSON.parse(storage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(macros) ? macros : [];
  } catch (e) {
    console.warn('could not read the print macros saved in this browser', e);
    return [];
  }
}

function cleanSlackChannel(channel) {
  const id = `${(channel && channel.id) || ''}`.trim();
  if (!id) {
    return null;
  }
  return {name: `${(channel && channel.name) || ''}`.trim() || id, id};
}

export function cleanMacro(macro) {
  const baseline = macro.baseline || NO_BASELINE;
  return {
    id: macro.id || newMacroId(),
    name: macro.name || 'untitled macro',
    orientation: macro.orientation === 'landscape' ? 'landscape' : 'portrait',
    // the production days it was recorded for; older macros also carry the production they were
    // recorded against, which their steps still name through a {{production}} token
    baseline: {dates: sortedDates(baseline.dates)},
    steps: fillProductionToken(Array.isArray(macro.steps) ? macro.steps : [], baseline.production),
    starred: !!macro.starred,
    // where a render's pdfs go, and the slack channel ({name, id}) kept for when that is slack
    delivery: macro.delivery === 'slack' ? 'slack' : 'download',
    slackChannel: cleanSlackChannel(macro.slackChannel),
    // when it runs by itself and for which production (utils/macro-schedule.js); saved only, nothing runs it yet
    schedule: cleanSchedule(macro.schedule),
    createdAt: macro.createdAt || new Date().toISOString(),
    updatedAt: macro.updatedAt || macro.createdAt || new Date().toISOString(),
    // email of whoever last saved it (null for macros from before this was recorded)
    updatedBy: macro.updatedBy || null,
  };
}

function copyTarget(target) {
  return isCompleteTarget(target) ? {dates: sortedDates(target.dates)} : null;
}

/** a step keeps the route it was captured from so it can be re-templated once the dates are known */
function makeStep(route, baseline, filename) {
  const {name, path, query} = route;
  return {...captureStep(route, baseline), filename: filename || '', route: {name, path, query}};
}

/** the saved macro; scheduleCleared is the api saying the edit took the macro off its schedule */
async function saveMacroOnServer(macro) {
  const {data} = await api.put(`${MACROS_URL}/${encodeURIComponent(macro.id)}`, macro);
  const saved = cleanMacro(data);
  return data.scheduleCleared ? {...saved, scheduleCleared: true} : saved;
}

export default {
  namespaced: true,
  state: () => ({
    macros: [],
    // the list has been fetched from the server at least once
    loaded: false,
    loading: false,
    loadError: null,
    // {name, baseline: {dates} | null, orientation, steps: [step]} while recording
    recording: null,
    // open each downloaded pdf in a new tab (remembered per browser)
    openAfterSave: getStorage().getItem(OPEN_AFTER_SAVE_KEY) === 'true',
    // the macros list shows only starred macros (remembered per browser)
    starredOnly: getStorage().getItem(STARRED_ONLY_KEY) === 'true',
  }),
  getters: {
    isRecording: state => !!state.recording,
    macroById: state => id => state.macros.find(m => m.id === id),
  },
  mutations: {
    SET_MACROS(state, macros) {
      state.macros = macros.map(cleanMacro);
    },
    UPSERT_MACRO(state, macro) {
      const clean = cleanMacro(macro);
      const index = state.macros.findIndex(m => m.id === clean.id);
      if (index >= 0) {
        state.macros.splice(index, 1, clean);
      } else {
        state.macros.push(clean);
      }
    },
    REMOVE_MACRO(state, id) {
      state.macros = state.macros.filter(m => m.id !== id);
    },
    SET_LOADING(state, {loading, loaded = state.loaded, error = null}) {
      state.loading = loading;
      state.loaded = loaded;
      state.loadError = error;
    },
    SET_OPEN_AFTER_SAVE(state, value) {
      state.openAfterSave = !!value;
      try {
        getStorage().setItem(OPEN_AFTER_SAVE_KEY, `${state.openAfterSave}`);
      } catch (e) {
        console.warn('could not remember the open-after-save setting', e);
      }
    },
    SET_STARRED_ONLY(state, value) {
      state.starredOnly = !!value;
      try {
        getStorage().setItem(STARRED_ONLY_KEY, `${state.starredOnly}`);
      } catch (e) {
        console.warn('could not remember the starred-only setting', e);
      }
    },
    SET_RECORDING(state, recording) {
      state.recording = recording;
    },
    SET_RECORDING_BASELINE(state, baseline) {
      if (state.recording) {
        state.recording.baseline = baseline;
      }
    },
    ADD_RECORDED_STEP(state, step) {
      if (state.recording) {
        state.recording.steps.push(step);
      }
    },
    REMOVE_RECORDED_STEP(state, index) {
      if (state.recording) {
        state.recording.steps.splice(index, 1);
      }
    },
  },
  actions: {
    /**
     * Load the macros from the server. The first time a browser that still holds
     * macros from before they moved to the server sees an empty server list, its
     * macros are carried over (the local copy is kept under another key as a backup).
     */
    async fetchMacros({state, commit, dispatch}) {
      if (state.loading) {
        return state.macros;
      }
      commit('SET_LOADING', {loading: true});
      try {
        const {data} = await api.get(MACROS_URL);
        commit('SET_MACROS', Array.isArray(data) ? data : []);
        commit('SET_LOADING', {loading: false, loaded: true});
      } catch (e) {
        console.error('could not load the print macros', e);
        commit('SET_LOADING', {loading: false, error: e.message || `${e}`});
        throw e;
      }
      const storage = getStorage();
      const local = loadLocalMacros(storage);
      if (state.macros.length === 0 && local.length > 0 && !storage.getItem(MIGRATED_KEY)) {
        try {
          const count = await dispatch('importMacros', local);
          storage.setItem(MIGRATED_KEY, new Date().toISOString());
          console.log(`moved ${count} print macros from this browser to the server`);
        } catch (e) {
          console.error('could not move the print macros saved in this browser to the server', e);
        }
      }
      return state.macros;
    },
    /**
     * The baseline production days are optional: when they are not given they are taken
     * from the first captured page whose url names them.
     */
    startRecording({commit}, {name, baseline, orientation = 'portrait'}) {
      commit('SET_RECORDING', {
        name: name || '',
        baseline: copyTarget(baseline),
        orientation,
        steps: [],
        startedAt: new Date().toISOString(),
      });
    },
    /**
     * Remember the page the admin is looking at as the next step; returns the step.
     * @param route {name, path, query}
     * @param filename what the admin wants the pdf called (before the production prefix)
     */
    recordStep({state, commit}, {route, filename}) {
      if (!state.recording) {
        throw new Error('not recording');
      }
      let baseline = state.recording.baseline;
      if (!isCompleteTarget(baseline)) {
        const inferred = inferBaseline(route);
        if (isCompleteTarget(inferred)) {
          baseline = inferred;
          commit('SET_RECORDING_BASELINE', inferred);
        }
      }
      const step = makeStep(route, baseline, filename);
      commit('ADD_RECORDED_STEP', step);
      return step;
    },
    removeRecordedStep({commit}, index) {
      commit('REMOVE_RECORDED_STEP', index);
    },
    /**
     * Finish recording and keep the macro; returns it (or null when nothing was captured).
     * Every step is templated again against the final baseline, so pages captured
     * before the dates were known get them matched too.
     */
    async stopRecording({state, commit}, {name} = {}) {
      const recording = state.recording;
      commit('SET_RECORDING', null);
      if (!recording || recording.steps.length === 0) {
        return null;
      }
      const baseline = recording.baseline;
      const steps = recording.steps.map(step => step.route ? makeStep(step.route, baseline, step.filename) : step);
      const macro = await saveMacroOnServer(cleanMacro({
        name: name || recording.name || `macro ${new Date().toLocaleString()}`,
        baseline: baseline || NO_BASELINE,
        orientation: recording.orientation,
        steps,
      }));
      commit('UPSERT_MACRO', macro);
      return macro;
    },
    cancelRecording({commit}) {
      commit('SET_RECORDING', null);
    },
    /** save changes to a macro ({id, ...changes}); returns the saved macro */
    async updateMacro({state, commit}, macro) {
      const current = state.macros.find(m => m.id === macro.id) || {};
      const saved = await saveMacroOnServer(cleanMacro({...current, ...macro}));
      commit('UPSERT_MACRO', saved);
      return saved;
    },
    async updateStep({state, dispatch}, {macroId, index, changes}) {
      const macro = state.macros.find(m => m.id === macroId);
      if (!macro || !macro.steps[index]) {
        return null;
      }
      const steps = macro.steps.map((step, i) => i === index ? {...step, ...changes} : step);
      return dispatch('updateMacro', {id: macroId, steps});
    },
    /** starring is a bookmark, not an edit: it does not count as saving the macro */
    async setStarred({commit}, {id, starred}) {
      const {data} = await api.put(`${MACROS_URL}/${encodeURIComponent(id)}/starred`, {starred: !!starred});
      commit('UPSERT_MACRO', data);
    },
    setStarredOnly({commit}, value) {
      commit('SET_STARRED_ONLY', value);
    },
    async deleteMacro({commit}, id) {
      await api.delete(`${MACROS_URL}/${encodeURIComponent(id)}`);
      commit('REMOVE_MACRO', id);
    },
    /**
     * A copy of a macro with some fields changed (e.g. another delivery as its default),
     * saved under a new name; returns the copy.
     */
    async duplicateMacro({state, commit}, {id, name, changes = {}}) {
      const current = state.macros.find(m => m.id === id);
      if (!current) {
        throw new Error('macro not found');
      }
      // a copy never runs on a schedule until it is turned on for it: that would print everything twice
      const schedule = current.schedule ? {...current.schedule, enabled: false} : null;
      const saved = await saveMacroOnServer(cleanMacro({
        ...current, schedule, ...changes, id: newMacroId(), name: name || `${current.name} (copy)`, starred: false,
        createdAt: undefined, updatedAt: undefined, updatedBy: undefined,
      }));
      commit('UPSERT_MACRO', saved);
      return saved;
    },
    /** import macros from a json export; each gets a new id so the export can be imported more than once */
    async importMacros({commit}, macros) {
      let count = 0;
      for (const macro of [].concat(macros)) {
        const saved = await saveMacroOnServer(cleanMacro({...macro, id: newMacroId()}));
        commit('UPSERT_MACRO', saved);
        count++;
      }
      return count;
    },
    setOpenAfterSave({commit}, value) {
      commit('SET_OPEN_AFTER_SAVE', value);
    },
  },
};
